import { ValidatedForm } from "../components/ValidatedForm";
import { useEffect, useRef, useState } from "react";
import { ArrowPathIcon, ArrowRightIcon, CheckCircleIcon, ClipboardDocumentCheckIcon, FunnelIcon } from "@heroicons/react/24/outline";
import { useLocale } from "../i18n";
import { ErrorNotice } from "../components/AdminFeedback";
import { Fields } from "./Fields";
import {
  boundFields,
  eventRequest,
  operation,
  queryDefaults,
  querySchema,
} from "./api";
import { validate } from "./validation";
import { DataView } from "./DataView";
import { TeamDetails } from "./TeamDetails";
import { ResultTable } from "./ResultTable";
import { unitDetailsLabels, valueLabel } from "./labels";
import { readinessNextAction, readinessStatus, readinessTask, readinessTaskHref } from "./readiness";
import type { Context, RecordValue } from "./types";
export function ReadPanel({
  name,
  title,
  context,
  revision,
  onLoaded,
  onRefresh,
  onTask,
  onLock,
}: {
  name: string;
  title: string;
  context: Context;
  revision: number;
  onLoaded?: (name: string, data: RecordValue) => void;
  onRefresh?: () => void;
  onTask?: (tab: string, read?: string, write?: string) => void;
  onLock?: (locked: boolean) => void;
}) {
  const { w, lang } = useLocale();
  const op = operation(name);
  const [query, setQuery] = useState<RecordValue>(() =>
    queryDefaults(op, context),
  );
  const [applied, setApplied] = useState<RecordValue>(() =>
    ({ ...queryDefaults(op, context), cursor: "" }),
  );
  const [data, setData] = useState<RecordValue | null>(null);
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [live, setLive] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [lastRead, setLastRead] = useState<{ scope: string; at: number } | null>(null);
  const onLoadedRef = useRef(onLoaded);
  useEffect(() => { onLoadedRef.current = onLoaded; }, [onLoaded]);
  const key = JSON.stringify(applied);
  const queryKey = JSON.stringify(query);
  const resultView = name === "result" || name === "roundLive";
  const readScope = `${name}:${context.unit?.unitId}:${key}`;
  const missing = validate(querySchema(op), query, true);
  useEffect(() => {
    const changed = () => { setOnline(navigator.onLine); setRefreshRequired(true); };
    window.addEventListener("online", changed); window.addEventListener("offline", changed);
    return () => { window.removeEventListener("online", changed); window.removeEventListener("offline", changed); };
  }, []);
  useEffect(() => {
    if (validate(querySchema(op), query, true).length) {
      setApplied(query);
      return;
    }
    const timer = setTimeout(() => setApplied({ ...query, cursor: "" }), 300);
    return () => clearTimeout(timer);
  }, [queryKey, name]);
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (validate(querySchema(op), applied, true).length) {
      setData(null);
      setBusy(false);
      setError(null);
      return () => controller.abort();
    }
    setError(null);
    setData(null);
    async function load() {
      if (controller.signal.aborted) return;
      setBusy(true);
      try {
        const result = await eventRequest(
          op,
          applied,
          undefined,
          controller.signal,
        );
        if (!controller.signal.aborted) {
          setData(result);
          setError(null);
          setLastRead({ scope: readScope, at: Date.now() });
          setRefreshRequired(false);
          onLoadedRef.current?.(name, result);
          if (
            live &&
            !document.hidden &&
            (name === "roundLive" ||
              (name === "resultJob" &&
                ["QUEUED", "RUNNING"].includes(String(result.state))))
          )
            timer = setTimeout(() => void load(), 5000);
        }
      } catch (e) {
        if (!controller.signal.aborted) { setError(e); setRefreshRequired(true); }
      } finally {
        if (!controller.signal.aborted) setBusy(false);
      }
    }
    void load();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
    // Scope changes remount the panel; polling never overlaps a request.
  }, [key, refresh, revision, live, name, context.unit?.unitId, context.unit?.version]);
  const hidden = [
    ...boundFields(op, context),
    "cursor",
    "limit",
    "knownVersion",
    ...(name === "entries"
      ? [
          context.unit?.unitKind === "VOTING_ROUND"
            ? "registrationScopeId"
            : "roundId",
        ]
      : []),
  ];
  const filters = op.query.filter(field => !field.required && !hidden.includes(field.name));
  const filtering = filters.some(field => query[field.name] !== undefined && query[field.name] !== "");
  const emptyItems = data && Array.isArray(data.items) && data.items.length === 0;
  const emptyHints: Record<string, [string, string]> = {
    groups: ["أنشئ فريقاً من أدوات الفرق، ثم أضف أعضائه.", "Create a team from the participant tools, then add its members."],
    entries: ["لا توجد مشاركات مسجلة ضمن هذا النطاق.", "There are no registered entries in this scope."],
    entryCandidates: ["تظهر الفرق المؤهلة للتسجيل هنا بعد إعدادها.", "Teams appear here once they are ready to register."],
    roles: ["استخدم «إسناد دور» لإضافة دور ضمن هذه المرحلة.", "Use Assign role to add a role within this stage."],
    admins: ["استخدم «تفويض إدارة الوحدة» لإضافة مدير للمرحلة.", "Use Delegate unit management to add a stage manager."],
    resultSnapshots: ["احتسب النتيجة بعد ختم الجولات، ثم عد لمراجعة اللقطة.", "Compute the result after sealing the rounds, then return to review the snapshot."],
    audit: ["ستظهر إجراءات هذه المرحلة هنا عند تنفيذها.", "Actions for this stage appear here as they occur."],
  };
  return (
    <section className={`panel admin-pad admin-form event-read-panel${name === "readiness" ? " event-readiness-panel" : name === "groups" ? " event-groups-panel" : ""}`} aria-busy={busy}>
      <div className="panel-title">
        <h3>{name === "unit" ? unitDetailsLabels(context.unit?.unitKind, lang).heading : title}</h3>
        <div className="event-read-toolbar">{(name === "roundLive" || name === "resultJob") && (
          <label className="check-label">
            <input
              type="checkbox"
              checked={live}
              onChange={(e) => setLive(e.target.checked)}
            />
            {w("متابعة تلقائية", "Auto refresh")}
          </label>
        )}<button type="button" className="secondary event-read-refresh" disabled={busy || missing.length > 0} onClick={() => { setApplied({ ...query, cursor: "" }); if (onRefresh) onRefresh(); else setRefresh(n => n + 1); }}><ArrowPathIcon aria-hidden="true" />{busy ? w("جارٍ التحميل…", "Loading…") : w("تحديث", "Refresh")}</button></div>
      </div>
      <ValidatedForm
        onSubmit={(e) => {
          e.preventDefault();
          setApplied({ ...query, cursor: "" });
          setRefresh((n) => n + 1);
        }}
      >
        <Fields
          schema={querySchema(op)}
          value={query}
          onChange={setQuery}
          context={context}
          hidden={[...hidden, ...op.query.filter(field => !field.required).map(field => field.name)]}
        />
        {filters.length > 0 && <details className="event-read-filters"><summary><FunnelIcon aria-hidden="true" />{w("تصفية العرض", "Filter this view")}{filtering && <span>{w("مفعّلة", "Active")}</span>}</summary>
          <Fields schema={querySchema(op)} value={query} onChange={setQuery} context={context} hidden={[...hidden, ...op.query.filter(field => field.required).map(field => field.name)]} />
        </details>}
      </ValidatedForm>
      <ErrorNotice error={error} operation="read" />
      {resultView && <div className="event-result-freshness" role="status" aria-live="polite">
        {lastRead?.scope === readScope && <p>{w("آخر قراءة ناجحة", "Last successful read")}: <time dateTime={new Date(lastRead.at).toISOString()}>{new Date(lastRead.at).toLocaleString(lang)}</time> · <bdi>{Intl.DateTimeFormat().resolvedOptions().timeZone}</bdi></p>}
        {!online ? <p className="info-box">{w("انقطع الاتصال. أي بيانات معروضة هي آخر قراءة محفوظة، ولا تؤكد الحالة الحالية. أعد التحديث بعد عودة الاتصال.", "Connection lost. Any displayed data is the last saved read and does not confirm the current state. Refresh after reconnecting.")}</p> : (refreshRequired || error) ? <p className="info-box">{w("يلزم تحديث النتائج للتحقق من الحالة الحالية؛ لم تتأكد قراءة جديدة بعد.", "Refresh the results to verify the current state; a new read has not yet been confirmed.")}</p> : null}
      </div>}
      {missing.length > 0 && <p className="event-read-empty">{w("اختر العنصر المطلوب أعلاه لعرض التفاصيل.", "Choose the required item above to see its details.")}</p>}
      {busy && !data && <p className="event-read-empty" role="status">{w("جارٍ تحميل التفاصيل…", "Loading details…")}</p>}
      {data && (
        <div className="read-data">
          {name === "result" ? <ResultTable data={data} context={context} /> : name === "readiness" ? <Readiness data={data} context={context} onTask={onTask} /> : emptyItems ? <div className="event-read-empty"><strong>{filtering ? w("لا توجد نتائج تطابق التصفية", "No results match these filters") : w("لا توجد عناصر حتى الآن", "No items yet")}</strong><p>{filtering ? w("جرّب توسيع البحث أو أزل التصفية.", "Try a broader search or remove the filters.") : emptyHints[name] ? w(...emptyHints[name]) : w("ستظهر العناصر هنا بعد إضافتها.", "Items will appear here after they are added.")}</p>{filtering && <button type="button" className="secondary" onClick={() => setQuery(Object.fromEntries(Object.entries(query).filter(([key]) => !filters.some(field => field.name === key))))}>{w("إزالة التصفية", "Clear filters")}</button>}</div> : name === "groups" ? <TeamDetails key={`${key}:${refresh}:${revision}`} data={data} context={context} onLock={onLock} /> : <DataView data={data} context={context} />}
          {data.nextCursor && (
            <button
              className="secondary"
              disabled={busy}
              onClick={() =>
                setApplied({ ...applied, cursor: data.nextCursor! })
              }
            >
              {w("الصفحة التالية", "Next page")}
            </button>
          )}
          {applied.cursor && (
            <button
              className="text-button"
              disabled={busy}
              onClick={() => setApplied({ ...applied, cursor: "" })}
            >
              {w("الصفحة الأولى", "First page")}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function Readiness({ data, context, onTask }: {
  data: RecordValue; context: Context; onTask?: (tab: string, read?: string, write?: string) => void;
}) {
  const { w, lang } = useLocale();
  const status = readinessStatus(data, context.unit);
  const issues = status === "blocked" && Array.isArray(data.issues) ? data.issues.map(String) : [];
  const ready = status === "ready";
  const nextAction = onTask ? readinessNextAction(data, context) : null;
  const tasks = issues.map(issue => readinessTask(issue, context));
  const firstActionIndex = tasks.findIndex(task => task !== null);
  const Icon = ready ? CheckCircleIcon : ClipboardDocumentCheckIcon;
  const titles = {
    ready: ["اكتملت متطلبات الجاهزية", "Readiness requirements complete"],
    blocked: ["أكمل المتطلبات التالية", "Complete these requirements"],
    stale: ["يلزم تحديث فحص الجاهزية", "Refresh the readiness check"],
    unknown: ["لم تُحسم الجاهزية بعد", "Readiness is not confirmed"],
  } satisfies Record<string, [string, string]>;
  const hints = {
    ready: nextAction
      ? ["راجع الخطوة التالية قبل تنفيذها. فحص الجاهزية لا يغيّر حالة المرحلة.", "Review the next step before applying it. This readiness check does not change the stage status."]
      : ["لا توجد متطلبات ناقصة في هذا الفحص. تبقى حالة المرحلة كما هي.", "This check has no outstanding requirements. The stage status remains unchanged."],
    blocked: ["انتقل مباشرة إلى كل إعداد، ثم حدّث الفحص بعد حفظ التغييرات.", "Open each setting below, then refresh this check after saving your changes."],
    stale: ["هذا الفحص لا يطابق النسخة المعروضة. حدّث الفحص لعرض المتطلبات الحالية.", "This check does not match the displayed version. Refresh it to see the current requirements."],
    unknown: ["حدّث الفحص للتأكد من المتطلبات. يمكنك مراجعة استجابة الفحص في التفاصيل.", "Refresh to confirm the requirements. The check response is available in the details."],
  } satisfies Record<string, [string, string]>;
  return <div className="event-readiness">
    <div className={`readiness-summary${ready ? " is-ready" : ""}`} role="status">
      <Icon aria-hidden="true" />
      <div><h4>{context.readOnly && status === "blocked" ? w("متطلبات لم تُستكمل", "Incomplete requirements") : w(...titles[status])}</h4><p>{context.readOnly ? w("هذا عرض لمتطلبات المرحلة المحفوظة. تعديل الإعدادات متاح بعد تجديد الاشتراك.", "These are the stage’s saved requirements. Settings can be changed after subscription renewal.") : w(...hints[status])}</p></div>
    </div>
    {nextAction && onTask && <button type="button" className="readiness-task-action is-primary readiness-next-action" onClick={() => onTask(nextAction.tab, nextAction.read, nextAction.write)}>{w(...nextAction.title)}<ArrowRightIcon aria-hidden="true" /></button>}
    {!!issues.length && <ol className="readiness-tasks" aria-label={w("متطلبات الجاهزية المتبقية", "Remaining readiness requirements")}>{issues.map((issue, index) => {
      const text = valueLabel(issue, "issues", lang);
      const task = tasks[index];
      const href = task ? readinessTaskHref(task, context) : undefined;
      return <li className="readiness-task" key={`${issue}-${index}`}>
        <span className="readiness-task-number" aria-hidden="true">{(index + 1).toLocaleString(lang)}</span>
        <div className="readiness-task-copy"><strong>{task ? w(...task.title) : w("متطلب يحتاج إلى مراجعة", "A requirement needs review")}</strong>
          <p>{text === issue ? w("راجع بيانات المرحلة وتفاصيل الفحص لهذا المتطلب.", "Review the stage data and check details for this requirement.") : text}</p>
        </div>
        {onTask && task && (href
          ? <a className={`readiness-task-action${index === firstActionIndex ? " is-primary" : ""}`} href={href}>{w(...task.action)}<ArrowRightIcon aria-hidden="true" /></a>
          : <button type="button" className={`readiness-task-action${index === firstActionIndex ? " is-primary" : ""}`} onClick={() => onTask(task.tab, task.read, task.write)}>{w(...task.action)}<ArrowRightIcon aria-hidden="true" /></button>)}
      </li>;
    })}</ol>}
    <details className="event-advanced readiness-diagnostics"><summary>{w("تفاصيل الفحص", "Check details")}</summary><DataView data={data} context={context} technical /></details>
  </div>;
}
